import React from "react";
import { Card, CardBody, Spinner, Button } from "reactstrap";
import {
    BarChart2,
    TrendingUp,
    Target,
    RefreshCw
} from "react-feather";
import { useAlertsOverview } from "../../Hooks/useAlertsOverview";
import { useDataFetch } from "../../Hooks/Use-data-fetch";

/**
 * Presentación de cada tipo de insight.
 *
 * Las claves son los INSIGHT_TYPES de insight-ms. El título es fijo (describe
 * el tipo) y el texto lo redacta el detector con las cifras reales del período,
 * así que aquí no se arma ninguna frase.
 */
const INSIGHT_PRESENTATION = {
    NETWORK_CONCENTRATION: {
        icon: BarChart2,
        color: "#3B82F6",
        title: "Concentración de ventas"
    },
    PARETO_8020: {
        icon: TrendingUp,
        color: "#22C55E",
        title: "Usuarios de alto valor"
    },
    REGION_SALES_VARIATION: {
        icon: Target,
        color: "#F59E0B",
        title: "Oportunidad detectada"
    }
};

// Orden de la maqueta. Se respeta sin importar en qué orden lleguen.
const INSIGHT_ORDER = [
    "NETWORK_CONCENTRATION",
    "PARETO_8020",
    "REGION_SALES_VARIATION"
];

const money = (value) =>
    new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(
        Math.round(value || 0)
    );

/**
 * Cifras que sustentan el porcentaje del insight.
 *
 * Sin esto la tarjeta afirma "aumentaron un 160%" sin decir sobre qué base, y
 * no hay forma de saber si viene de una región que pasó de vender poquísimo a
 * poco. Los números salen de `metrics`, que ya los trae calculados.
 */
const buildDetail = (type, metrics) => {
    if (!metrics) return null;

    if (type === "NETWORK_CONCENTRATION") {
        if (metrics.approved_orders == null) return null;
        return [
            `${metrics.approved_orders} de ${metrics.network_approved_orders ?? 0} órdenes`,
            metrics.structure_size != null
                ? `${metrics.structure_size} usuarios en su estructura`
                : null
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (type === "PARETO_8020") {
        const top = metrics.top_line;
        if (!top) return null;
        return [
            `${money(top.points)} puntos de ${money(metrics.lines_total)}`,
            metrics.total_lines != null
                ? `${metrics.total_lines} líneas comparadas`
                : null
        ]
            .filter(Boolean)
            .join(" · ");
    }

    if (type === "REGION_SALES_VARIATION") {
        const region = metrics.variations?.[0];
        if (!region) return null;
        return (
            `${money(region.previous_total)} → ${money(region.current_total)}` +
            ` · ${region.orders_previous ?? 0} → ${region.orders_current ?? 0} órdenes`
        );
    }

    return null;
};

const AutomaticInsightsCard = () => {

    const { insights, insightsLoading, error } = useAlertsOverview();

    // Recalcular a mano. El motor corre solo a las 03:30, así que sin esto
    // había que esperar al otro día para ver el efecto de un cambio de umbral.
    const { postData, invalidateQuery } = useDataFetch();

    const { mutate: runEngine, isPending: running } = postData(
        "/alerts/run",
        {},
        {
            onSuccess: () => {
                invalidateQuery(["insights"]);
                invalidateQuery(["alerts-summary"]);
                invalidateQuery(["alerts"]);
            }
        }
    );

    // Un insight por tipo: el backend devuelve el más reciente de cada uno
    // primero, así que el primero que aparece es el que se muestra.
    const byType = new Map();
    for (const insight of insights) {
        if (!byType.has(insight.type)) byType.set(insight.type, insight);
    }

    const rows = INSIGHT_ORDER
        .filter((type) => byType.has(type) && INSIGHT_PRESENTATION[type])
        .map((type) => {
            const insight = byType.get(type);

            return {
                ...INSIGHT_PRESENTATION[type],
                type,
                description: insight.text,
                // El gateway manda en `title` el subtitle del detector: para
                // concentración es "Nombre (CÓDIGO)" del líder, y para región
                // el período contra el que se comparó. Antes se descartaba, y
                // por eso la tarjeta decía "este líder" sin decir cuál.
                subject: insight.title,
                detail: buildDetail(type, insight.metrics)
            };
        });

    return (

        <Card className="border-0 shadow-sm w-100">

            <CardBody className="p-4">

                <div className="d-flex justify-content-between align-items-center mb-3">

                    <h5
                        className="fw-bold mb-0"
                        style={{
                            color: "#101828",
                            fontSize: 17
                        }}
                    >
                        Insights Automáticos
                    </h5>

                    <Button
                        color="link"
                        size="sm"
                        className="p-0 d-flex align-items-center"
                        disabled={running}
                        onClick={() => runEngine({})}
                        title="Vuelve a calcular alertas e insights con los datos de ahora"
                        style={{ fontSize: 12, textDecoration: "none" }}
                    >
                        {running
                            ? <Spinner size="sm" className="me-1" style={{ width: 12, height: 12 }} />
                            : <RefreshCw size={14} className="me-1" />}
                        {running ? "Calculando..." : "Recalcular"}
                    </Button>

                </div>

                {
                    insightsLoading && (
                        <div className="text-center py-4">
                            <Spinner size="sm" />
                        </div>
                    )
                }

                {
                    !insightsLoading && (error || rows.length === 0) && (
                        <div
                            style={{
                                color: "#667085",
                                fontSize: 13
                            }}
                        >
                            {
                                error
                                    ? "No se pudieron cargar los insights."
                                    : "Aún no hay insights de la última corrida."
                            }
                        </div>
                    )
                }

                {

                    !insightsLoading && !error && rows.map((item, index) => {

                        const Icon = item.icon;

                        return (

                            <div
                                key={item.type}
                                className="d-flex align-items-start py-3"
                                style={{
                                    borderBottom:
                                        index !== rows.length - 1
                                            ? "1px solid #EAECF0"
                                            : "none"
                                }}
                            >

                                <div
                                    style={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 8,
                                        background: "#F8F9FC",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 15,
                                        flexShrink: 0
                                    }}
                                >

                                    <Icon
                                        size={20}
                                        color={item.color}
                                    />

                                </div>

                                <div
                                    style={{
                                        flex: 1
                                    }}
                                >

                                    <div
                                        style={{
                                            color: "#101828",
                                            fontWeight: 600,
                                            fontSize: 14,
                                            marginBottom: 4
                                        }}
                                    >
                                        {item.title}
                                    </div>

                                    <div
                                        style={{
                                            color: "#667085",
                                            fontSize: 13,
                                            lineHeight: "20px"
                                        }}
                                    >
                                        {item.description}
                                    </div>

                                    {
                                        item.subject && (
                                            <div
                                                style={{
                                                    color: "#344054",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    marginTop: 4
                                                }}
                                            >
                                                {item.subject}
                                            </div>
                                        )
                                    }

                                    {
                                        item.detail && (
                                            <div
                                                style={{
                                                    color: "#98A2B3",
                                                    fontSize: 12,
                                                    marginTop: 2
                                                }}
                                            >
                                                {item.detail}
                                            </div>
                                        )
                                    }

                                </div>

                            </div>

                        );

                    })

                }

            </CardBody>

        </Card>

    );

};

export default AutomaticInsightsCard;
